import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  Signal,
  computed,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { applicationDebounceTime } from '@anduin/shared';
import { BehaviorSubject, fromEvent, merge, tap } from 'rxjs';
import { MetricUnit } from '../../models/metric-unit.model';

interface ComponentMetricUnit extends MetricUnit {
  percentage: number;
  icon: string;
  highlightClass: string;
}

interface VerticalDifCheck {
  latestTop: number;
  wrappedIndexes: number[];
}

const VERTICAL_LIMIT = 10;

export type MetricRange = 'Weekly' | 'Monthly' | 'Yearly' | 'Quarterly';

@Component({
  selector: 'app-email-delivery-hub-insights',
  templateUrl: './email-delivery-hub-insights.component.html',
  styleUrl: './email-delivery-hub-insights.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EmailDeliveryHubInsightsComponent implements AfterViewInit, OnDestroy {
  container: Signal<ElementRef<HTMLElement>> = viewChild('container');

  metricsToShow = input.required<MetricUnit[]>();
  total = input.required<MetricUnit>();
  range = input<MetricRange>();
  comparisonDateRange = input<string>();

  shouldPaginate = input(false);

  // Pagination signals
  currentPage = signal(0);
  numberOfPages = signal(0);
  hasMultiplePages = signal(false);
  wrappedIndexes = signal([]);
  isTransitioning = signal(false);

  pages = signal([]);

  units = computed(() => this._createUnits());
  containerWidth = computed(() => `${(this.numberOfPages() + 1) * 100}%`);
  reachedThePageLimit = computed(() => this.currentPage() === this.numberOfPages());
  translateXValue = computed(() => this.currentPage() * (100 / (this.numberOfPages() + 1)) * -1);

  pageWidth = computed(() => 100 / (this.numberOfPages() || 1) + '%');

  private readonly destroyRef = inject(DestroyRef);

  private readonly containerResize$ = new BehaviorSubject(undefined);
  private readonly resizeObserver = new ResizeObserver(this._onContainerResize.bind(this));

  private resize$ = merge(fromEvent(window, 'resize'), this.containerResize$).pipe(
    applicationDebounceTime(),
    takeUntilDestroyed(this.destroyRef),
    tap(() => this._checkForWrapping()),
  );

  private _createUnits(): ComponentMetricUnit[] {
    const total = this.total();

    return this.metricsToShow().map((each) => ({
      ...each,
      percentage: total.value === 0 ? 0 : each.value / total.value,
      icon: this._getMetricIcon(each),
      highlightClass: this._getMetricHighlightClass(each),
    }));
  }

  private _getMetricIcon(metric: MetricUnit): string {
    return metric.differFromLastMetric > 0 ? 'arrow-up' : 'arrow-down';
  }

  private _getMetricHighlightClass(metric: MetricUnit): string {
    const isPositive = metric.differFromLastMetric > 0;
    const isInverted = metric.invertedSentiment ?? false;
    const isGoodTrend = isInverted ? !isPositive : isPositive;

    return `email-delivery-hub-insights-card-highlight-${isGoodTrend ? 'up' : 'down'}`;
  }

  ngAfterViewInit(): void {
    setTimeout(() => this._checkForWrapping(), 100);

    this.resizeObserver.observe(this.container().nativeElement);
    this.resize$.subscribe();
  }

  ngOnDestroy(): void {
    this.resizeObserver.disconnect();
  }

  private _onContainerResize() {
    this.containerResize$.next(true);
  }

  private _checkForWrapping(): void {
    if (!this.shouldPaginate()) {
      return;
    }

    if (!this.container() || !this.container().nativeElement) {
      return;
    }

    const container = this.container().nativeElement;
    const cardItems = container.querySelectorAll('#page-one .email-delivery-hub-insights-card');
    const cards = Array.from(cardItems);

    if (cards.length === 0) return;

    const firstTop = cards[0].getBoundingClientRect().top;
    const hasWrapping = !!cards.find((card) => card.getBoundingClientRect().top > firstTop + VERTICAL_LIMIT);
    const { wrappedIndexes } = cards.reduce(this._byVerticalDiff, {
      latestTop: firstTop,
      wrappedIndexes: [],
    } as VerticalDifCheck);

    this.wrappedIndexes.set(wrappedIndexes);
    this.hasMultiplePages.set(hasWrapping);
    this.numberOfPages.set(wrappedIndexes.length);

    this._calculatePages();
  }

  private _byVerticalDiff(prev: VerticalDifCheck, current: Element, currentIndex: number): VerticalDifCheck {
    const currentTop = current.getBoundingClientRect().top;

    if (!(currentTop > prev.latestTop + VERTICAL_LIMIT)) {
      return prev;
    }

    return {
      latestTop: currentTop,
      wrappedIndexes: [...prev.wrappedIndexes, currentIndex - 1],
    };
  }

  private _calculatePages(): void {
    const indexes = this.wrappedIndexes();

    this.pages.set(indexes.map((wrapIndex, arrIndex) => this._getSlicedUnits(wrapIndex, indexes[arrIndex + 1])));
  }

  private _getSlicedUnits(from: number, to: number): ComponentMetricUnit[] {
    return this.units().slice(from, to);
  }

  nextPage(): void {
    if (!this.hasMultiplePages()) return;

    this.currentPage.set(this.currentPage() + 1);
  }

  previousPage(): void {
    if (!this.hasMultiplePages()) return;

    this.currentPage.set(0);
  }

  getAbsoluteValue(value: number): number {
    return Math.abs(value);
  }
}
